import React from "react";
import { useComponentTranslation } from "@/services/i18n";
import { FiMessageSquare, FiZap, FiFileText } from "react-icons/fi";

const translations = {
  en: {
    title: "Application",
    subtitle: "Review the candidate's application details and responses",
    cover_letter: "Cover Letter",
    cover_letter_desc: "Candidate's motivation and cover letter",
    custom_questions: "Screening Questions",
    custom_questions_desc: "Answers to custom application questions",
    ai_questions: "AI Interview Questions",
    ai_questions_desc: "AI-generated questions and candidate responses",
    no_answer: "No answer provided",
    no_questions: "No application details available",
    no_custom_questions: "No screening questions were asked for this application",
    no_cover_letter: "No cover letter provided",
  },
  fr: {
    title: "Candidature",
    subtitle: "Consultez les détails de la candidature et les réponses du candidat",
    cover_letter: "Lettre de Motivation",
    cover_letter_desc: "Motivation et lettre de motivation du candidat",
    custom_questions: "Questions de Présélection",
    custom_questions_desc: "Réponses aux questions personnalisées de candidature",
    ai_questions: "Questions d'Entretien IA",
    ai_questions_desc: "Questions générées par l'IA et réponses du candidat",
    no_answer: "Aucune réponse fournie",
    no_questions: "Aucun détail de candidature disponible",
    no_custom_questions: "Aucune question de présélection n'a été posée pour cette candidature",
    no_cover_letter: "Aucune lettre de motivation fournie",
  },
};

export default function QuestionsTab({ application, candidate }) {
  const { t } = useComponentTranslation(translations);
  const customAnswers = application?.custom_answers || [];
  const aiQuestionsAndAnswers = application?.job_application_ai_qna || [];
  const coverLetter = candidate?.cover_letter;

  const hasCustomAnswers = customAnswers.length > 0;
  const hasAiQuestions = aiQuestionsAndAnswers.length > 0;
  const hasCoverLetter = coverLetter && coverLetter.trim() !== "";

  if (!hasCustomAnswers && !hasAiQuestions && !hasCoverLetter) {
    return (
      <div className="p-5 bg-gray-50 rounded-lg border border-gray-200 text-center">
        <p className="text-sm text-gray-500">{t("no_questions")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Title and Subtitle */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">{t("title")}</h2>
        <p className="text-sm text-gray-500 mt-1">{t("subtitle")}</p>
      </div>

      {/* Cover Letter */}
      {hasCoverLetter && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <FiFileText className="w-5 h-5 text-green-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t("cover_letter")}</h3>
              <p className="text-sm text-gray-500">{t("cover_letter_desc")}</p>
            </div>
          </div>
          <div className="border border-green-100 rounded-lg p-5 bg-green-50/50">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{coverLetter}</p>
          </div>
        </div>
      )}

      {/* Custom Screening Questions */}
      {hasCustomAnswers && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <FiMessageSquare className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t("custom_questions")}</h3>
              <p className="text-sm text-gray-500">{t("custom_questions_desc")}</p>
            </div>
          </div>
          <div className="space-y-4">
            {customAnswers.map((qa, index) => (
              <div key={qa.question_id || index} className="border border-blue-100 rounded-lg p-5 bg-blue-50/50">
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-blue-100 text-blue-700 rounded flex items-center justify-center text-xs font-medium">
                      {index + 1}
                    </div>
                    <h4 className="text-sm font-semibold text-gray-800">{qa.question}</h4>
                  </div>
                </div>
                <div className="mt-3 pl-8">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{qa.answer || t("no_answer")}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI-Generated Questions */}
      {hasAiQuestions && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <FiZap className="w-5 h-5 text-purple-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t("ai_questions")}</h3>
              <p className="text-sm text-gray-500">{t("ai_questions_desc")}</p>
            </div>
          </div>
          <div className="space-y-4">
            {aiQuestionsAndAnswers.map((qa, index) => (
              <div key={index} className="border border-purple-100 rounded-lg p-5 bg-purple-50/50">
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-purple-100 text-purple-700 rounded flex items-center justify-center text-xs font-medium">
                      {index + 1}
                    </div>
                    <h4 className="text-sm font-semibold text-gray-800">{qa.question}</h4>
                  </div>
                </div>
                <div className="mt-3 pl-8">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{qa.answer || t("no_answer")}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

